import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, Circle, Store, Gauge } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import AiScore from "@/components/common/AiScore";

const ICON = {
  ready: { Icon: Check, cls: "text-success bg-success/10" },
  partial: { Icon: Clock, cls: "text-warning bg-warning/10" },
  todo: { Icon: Circle, cls: "text-muted-foreground bg-secondary" },
};

function NoStore() {
  const t = useT();
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <Store className="mx-auto h-10 w-10 text-muted-foreground" />
      <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
      <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
    </div>
  );
}

export default function AIReadiness() {
  const t = useT();
  const { store, loading } = useStore();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (store) api.get(`/readiness/${store.id}`).then(({ data }) => setData(data)).catch(() => {});
  }, [store]);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!store) return <NoStore />;

  const statusLabel = (s) => ({ ready: t("readiness.statusReady"), partial: t("readiness.statusPartial"), todo: t("readiness.statusTodo") }[s]);

  return (
    <div>
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <Gauge className="h-6 w-6 text-brand" />{t("readiness.title")}
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("readiness.subtitle")}</p>

      {data && (
        <div className="mt-8 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div className="min-w-0">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className="flex flex-col items-center rounded-lg border border-border bg-primary p-8 text-primary-foreground">
              <span className="text-xs uppercase tracking-widest text-primary-foreground/60">{t("readiness.score")}</span>
              <div className="my-4"><AiScore score={data.score} size="lg" /></div>
              <p className="text-sm text-primary-foreground/70">{t("readiness.ready", { count: data.ready_count, total: data.total })}</p>
            </motion.div>
            <div className="mt-4 rounded-lg border border-border bg-card p-5">
              <h3 className="font-display text-sm font-bold">{t("readiness.boostTitle")}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t("readiness.boostBody")}</p>
              <Link to="/dashboard/ai" className="mt-3 inline-flex rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition-transform hover:opacity-90 active:scale-95">
                {t("ai.generateAll")}
              </Link>
            </div>
          </div>

          <div className="min-w-0 space-y-2" data-testid="readiness-checklist">
            {data.items.map((it, i) => {
              const { Icon, cls } = ICON[it.status];
              return (
                <motion.div key={it.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}
                  className="flex items-start gap-3 rounded-lg border border-border bg-card p-4" data-testid={`readiness-item-${it.key}`}>
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{it.label}</p>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{statusLabel(it.status)}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{it.description}</p>
                    {it.status !== "ready" && (
                      <p className="mt-1 text-xs text-brand">{t("readiness.recommendedAction")}: {it.action}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
